import { lazy, type ReactNode } from 'react';
import {
  AlertCircle,
  BarChart3,
  BookOpen,
  Home,
  Search,
  Settings,
  Target,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import type { SessionMode, TrainingMode, TrainingSource, User } from '../types';

const DashboardPage = lazy(() => import('../components/pages/Dashboard').then((module) => ({ default: module.Dashboard })));
const GoalsPage = lazy(() => import('../components/pages/Goals').then((module) => ({ default: module.Goals })));
const ProgressPage = lazy(() => import('../components/pages/Progress').then((module) => ({ default: module.Progress })));
const LeaderboardPage = lazy(() =>
  import('../components/pages/Leaderboard').then((module) => ({ default: module.LeaderboardPage }))
);
const WordBrowserPage = lazy(() =>
  import('../components/pages/WordBrowser').then((module) => ({ default: module.WordBrowser }))
);
const MistakesJournalPage = lazy(() =>
  import('../components/pages/MistakesJournal').then((module) => ({ default: module.MistakesJournal }))
);
const ProfilePage = lazy(() => import('../components/pages/Profile').then((module) => ({ default: module.Profile })));
const AdminWordsPage = lazy(() =>
  import('../components/pages/AdminWords').then((module) => ({ default: module.AdminWords }))
);

export type AppPageId =
  | 'dashboard'
  | 'goals'
  | 'progress'
  | 'leaderboard'
  | 'browse'
  | 'mistakes'
  | 'profile'
  | 'admin-words';

export type AppNavigationPlacement = 'sidebar' | 'account' | 'hidden' | 'admin';

export interface AppPageRenderContext {
  activeUser: User;
  onStartTraining: (mode: TrainingMode, sessionMode?: SessionMode, source?: TrainingSource) => void;
  onViewMistakes: () => void;
  onViewLeaderboard: () => void;
}

export interface AppPageDefinition {
  id: AppPageId;
  labelKey: string;
  icon: LucideIcon;
  navigationPlacement: AppNavigationPlacement;
  requiresAdmin?: boolean;
  render: (context: AppPageRenderContext) => ReactNode;
}

export const DEFAULT_APP_PAGE_ID: AppPageId = 'dashboard';

const APP_PAGE_DEFINITIONS: AppPageDefinition[] = [
  {
    id: 'dashboard',
    labelKey: 'layout.dashboard',
    icon: Home,
    navigationPlacement: 'sidebar',
    render: ({ activeUser, onStartTraining, onViewMistakes, onViewLeaderboard }) => (
      <DashboardPage
        user={activeUser}
        onStartTraining={onStartTraining}
        onViewMistakes={onViewMistakes}
        onViewLeaderboard={onViewLeaderboard}
      />
    ),
  },
  {
    id: 'goals',
    labelKey: 'layout.goals',
    icon: Target,
    navigationPlacement: 'sidebar',
    render: ({ activeUser }) => <GoalsPage user={activeUser} />,
  },
  {
    id: 'progress',
    labelKey: 'layout.progress',
    icon: BarChart3,
    navigationPlacement: 'sidebar',
    render: () => <ProgressPage />,
  },
  {
    id: 'leaderboard',
    labelKey: 'layout.leaderboard',
    icon: Trophy,
    navigationPlacement: 'sidebar',
    render: () => <LeaderboardPage />,
  },
  {
    id: 'browse',
    labelKey: 'layout.browse',
    icon: Search,
    navigationPlacement: 'sidebar',
    render: () => <WordBrowserPage />,
  },
  {
    id: 'mistakes',
    labelKey: 'layout.mistakes',
    icon: AlertCircle,
    navigationPlacement: 'sidebar',
    render: ({ activeUser, onStartTraining }) => (
      <MistakesJournalPage userId={activeUser.uid} onStartTraining={onStartTraining} />
    ),
  },
  {
    id: 'profile',
    labelKey: 'layout.settings',
    icon: Settings,
    navigationPlacement: 'account',
    render: () => <ProfilePage />,
  },
  {
    id: 'admin-words',
    labelKey: 'layout.words',
    icon: BookOpen,
    navigationPlacement: 'admin',
    requiresAdmin: true,
    render: () => <AdminWordsPage />,
  },
];

export function buildAppPagePath(pageId: AppPageId) {
  return `/app/${pageId}`;
}

export function resolveAppPage(pageId: string | undefined, isAdmin = false): AppPageDefinition | null {
  if (!pageId) {
    return null;
  }

  const page = APP_PAGE_DEFINITIONS.find((item) => item.id === pageId);
  if (!page) {
    return null;
  }

  if (page.requiresAdmin && !isAdmin) {
    return null;
  }

  return page;
}

export function getNavigationPages(placement: AppNavigationPlacement, isAdmin = false): AppPageDefinition[] {
  return APP_PAGE_DEFINITIONS.filter(
    (page) => page.navigationPlacement === placement && (!page.requiresAdmin || isAdmin),
  );
}

